import { useRequestSafetyHelp } from "@features/safety/mutations/useRequestSafetyHelp";
import { useId, useState } from "react";

type AskForAngelaSheetProps = {
  open: boolean;
  onClose: () => void;
};

export default function AskForAngelaSheet({ open, onClose }: AskForAngelaSheetProps) {
  const titleId = useId();
  const messageId = useId();
  const locationId = useId();
  const [message, setMessage] = useState("");
  const [shareLocation, setShareLocation] = useState(true);
  const [sent, setSent] = useState(false);
  const { mutate, isPending, error, reset } = useRequestSafetyHelp();

  if (!open) {
    return null;
  }

  const handleClose = () => {
    setMessage("");
    setShareLocation(true);
    setSent(false);
    reset();
    onClose();
  };

  const handleSend = () => {
    mutate(
      { message, shareLocation },
      {
        onSuccess: () => setSent(true),
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      data-testid="ask-for-angela-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
        {sent ? (
          <>
            <h2 id={titleId} className="text-xl font-semibold text-gray-900">
              Help request sent
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Venue safety contacts have been alerted. If you are in immediate danger, call 999.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-6 w-full rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white"
              data-testid="ask-for-angela-done"
            >
              Close
            </button>
          </>
        ) : (
          <>
            <h2 id={titleId} className="text-xl font-semibold text-gray-900">
              Ask for Angela
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This alerts Fitties safety contacts for tonight&apos;s event. It is not an emergency service — call 999 if
              you are in immediate danger.
            </p>

            <label htmlFor={messageId} className="mt-4 block text-sm font-medium text-gray-800">
              Message (optional)
            </label>
            <textarea
              id={messageId}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900"
              data-testid="ask-for-angela-message"
            />

            <label className="mt-3 flex items-center gap-2 text-sm text-gray-800" htmlFor={locationId}>
              <input
                id={locationId}
                type="checkbox"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
                data-testid="ask-for-angela-share-location"
              />
              Share my location
            </label>

            {error ? (
              <p className="mt-3 text-sm text-red-600" data-testid="ask-for-angela-error">
                {error instanceof Error ? error.message : "Could not send help request"}
              </p>
            ) : null}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700"
                data-testid="ask-for-angela-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={isPending}
                className="flex-1 rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="ask-for-angela-send"
              >
                {isPending ? "Sending…" : "Send help request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
